import { useEffect, useRef, useState } from "react";
import {
    Card,
    CardBody,
    CardFooter,
    Button,
    Alert,
    Typography,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import FormikPasswordInput from "components/formik-password-input/formik-password-input";
import FormikInput from "components/form/formik-input";
import * as Yup from "yup";
import { Link, useNavigate, useSearchParams } from "react-router";
import Image from "components/image";
import logo from "assets/img/logos/gobby-anim.gif";
import { toast } from "react-toastify";
import Trademark from "components/Trademark";
import { apiFetch, fetcherFull } from "utils/api";
import { isApiError } from "utils/api-error";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";
import { totpField } from "utils/validation";

export default function ResetConfirmPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const passwordInputRef = useRef(null);
    const { isBlocked: resetBlocked } = useRateLimit();
    const [isTokenValid, setIsTokenValid] = useState(null); // null: loading, true: valid, false: invalid
    const [errorMessage, setErrorMessage] = useState('');
    const [resetEmail, setResetEmail] = useState('');

    const token = searchParams.get('token');

    const initialPasswordValues = {
        password: "",
        confirmPassword: "",
        totp: "",
    };

    const passwordValidationSchema = Yup.object().shape({
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            // Passwords go to the wire verbatim (#1097), so nothing downstream
            // reduces an all-whitespace value to empty any more. The backend
            // checks length only, so this is the sole rejection point.
            .matches(/\S/, "Password cannot be only spaces")
            .required("Password is required"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], "Passwords must match")
            .required("Confirm password is required"),
        // Optional: only 2FA accounts need a code.
        totp: totpField.nullable(),
    });

    // Verify token on component mount
    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setIsTokenValid(false);
                setErrorMessage('Invalid or missing token');
                return;
            }
            try {
                // Mirrors invite-accept.jsx validateToken (#1112): a
                // malformed 2xx body degrades to "no email", never to
                // "invalid token". Non-2xx still throws via apiFetch.
                const response = await apiFetch(`/v2/password-reset/${encodeURIComponent(token)}`, {
                    method: "GET",
                    // Asks a newer backend for the account email; older ones
                    // keep replying 204, so both ends ship in any order.
                    headers: { Accept: "application/json" },
                    timeoutMs: 30000,
                });
                if (response.status === 204) {
                    // Older backend: token valid, no account email to show (#1112).
                    setIsTokenValid(true);
                } else {
                    const data = await response.json().catch(() => null);
                    if (data?.email) {
                        setResetEmail(data.email);
                    }
                    setIsTokenValid(true);
                }
            } catch (error) {
                console.error('Token verification error');
                setIsTokenValid(false);
                setErrorMessage('Invalid or expired token');
            }
        };
        verifyToken();
    }, [token]);

    // Auto-focus on password input when token is valid
    useEffect(() => {
        if (isTokenValid && passwordInputRef.current) {
            passwordInputRef.current.focus();
        }
    }, [isTokenValid]);

    const submitPasswordReset = async (password, totp) => {
        // Password verbatim; the TOTP code is still sanitised, it is not a
        // credential we store. See #1097 / gobby-app/gobbyapi#466.
        // Internal whitespace goes too: authenticator apps display codes as
        // "123 456", and that is how people paste them (#1077).
        const sanitisedTotp = totp ? sanitiseInput(totp).replace(/\s+/g, '') : null;

        if (!password) {
            toast.error('Password is required');
            throw new Error('Password is required');
        }

        const params = { password };

        if (sanitisedTotp) {
            params.totp = sanitisedTotp;
        }

        await fetcherFull(
            `/v2/password-reset/${encodeURIComponent(token)}`,
            "POST",
            params,
        );
    };

    const handlePasswordSubmit = async (values, { setSubmitting }) => {
        if (resetBlocked) {
            toast.error('Too many attempts. Please try again later.');
            setSubmitting(false);
            return;
        }
        // Reject passwords that sanitise to empty.
        if (!sanitiseInput(values.password)) {
            setErrorMessage('Password is required.');
            setSubmitting(false);
            return;
        }
        setErrorMessage('');
        try {
            await submitPasswordReset(values.password, values.totp);
            toast.success('Password reset successful');
            setTimeout(() => navigate('/login'), 1000);
        } catch (error) {
            if (isApiError(error) && error.status === 401) {
                setErrorMessage('Invalid or missing 2FA code. Enter the code from your authenticator app and try again.');
            } else {
                console.error('Password reset error');
                setErrorMessage('Failed to reset password. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (isTokenValid === null) {
        return (
            <div className="flex flex-col min-w-screen min-h-screen bg-white justify-center items-center">
                <Typography className="font-display text-center text-primary">
                    Checking your reset link...
                </Typography>
            </div>
        );
    }

    if (isTokenValid === false) {
        return (
            <div className="flex flex-col min-w-screen min-h-screen bg-white">
                <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                    <Image
                        className="sm:w-[250px] w-[0px]"
                        src={logo}
                        rounded={false}
                        raised={false}
                        alt="Gobby Logo"
                    />
                </div>
                <div className="flex min-h-fit justify-center">
                    <Card
                        className="card-inner filter-none sm:p-8 p-4"
                        shadow={false}
                    >
                        <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                            Forgot Your Password?
                        </Typography>
                        <Alert color="red" className="mt-4">
                            {errorMessage}
                        </Alert>
                        <Typography className="text-center mt-4 mb-4">
                            Please request a new password reset link.
                        </Typography>
                        <div className="flex flex-col justify-center items-center">
                            <Button
                                variant="outlined"
                                size="lg"
                                className="btn btn--secondary rounded-full font-display text-2xl flex items-center justify-center"
                                onClick={() => navigate('/resetpassword')}
                            >
                                Back to Password Reset
                            </Button>
                            <Link
                                className="w-full text-center mt-2"
                                to="/login"
                            >
                                <Typography
                                    className="my-2 btn btn--primary text-white"
                                    as="span"
                                >
                                    Back to Login
                                </Typography>
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-w-screen min-h-screen bg-white">
            <div className="basis-0 h-[250px] md:basis-1/3 pl-3 pt-3">
                <Image
                    className="sm:w-[250px] w-[0px]"
                    src={logo}
                    rounded={false}
                    raised={false}
                    alt="Gobby Logo"
                />
            </div>
            <div className="flex min-h-fit justify-center">
                <Card
                    className="card-inner filter-none sm:p-8 p-4"
                    shadow={false}
                >
                    <Typography className="font-display text-center md:text-xl lg:content-center md:mt-2 text-primary">
                        Set New Password
                        <Trademark />
                    </Typography>
                    <Formik
                        initialValues={{ email: resetEmail, ...initialPasswordValues }}
                        onSubmit={handlePasswordSubmit}
                        validationSchema={passwordValidationSchema}
                    >
                        {({ isValid, dirty, isSubmitting }) => (
                            <Form>
                                <CardBody className="card-inner-body font-body my-4 py-2">
                                    <div className="my-4 px-0">
                                        {resetEmail && (
                                            <Typography
                                                as="div"
                                                className="px-4 rounded-2xl mb-4"
                                            >
                                                <FormikInput
                                                    name="email"
                                                    label="Email"
                                                    autoComplete="username"
                                                    readOnly
                                                />
                                            </Typography>
                                        )}
                                        <Typography
                                            as="div"
                                            className="px-4 rounded-2xl mb-4"
                                        >
                                            <FormikPasswordInput
                                                name="password"
                                                label="New Password"
                                                ref={passwordInputRef}
                                                aria-label="Enter your new password"
                                                showStrength={true}
                                                autoComplete="new-password"
                                            />
                                        </Typography>
                                        <Typography
                                            as="div"
                                            className="px-4 rounded-2xl mb-4"
                                        >
                                            <FormikPasswordInput
                                                name="confirmPassword"
                                                label="Confirm New Password"
                                                aria-label="Confirm your new password"
                                                autoComplete="new-password"
                                            />
                                        </Typography>
                                        <Typography
                                            as="div"
                                            className="px-4 rounded-2xl mb-4"
                                        >
                                            <FormikInput
                                                autoComplete="one-time-code"
                                                className="font-display text-lg bg-white pt-2"
                                                name="totp"
                                                type="text"
                                                color="black"
                                                label="Authentication Code"
                                            />
                                            <Typography
                                                as="div"
                                                variant="small"
                                                className="text-gray-500 mt-1"
                                            >
                                                Only needed if you have
                                                two-factor authentication
                                                set up.
                                            </Typography>
                                        </Typography>
                                        {errorMessage && (
                                            <Alert color="red" className="mt-4">
                                                {errorMessage}
                                            </Alert>
                                        )}
                                    </div>
                                </CardBody>
                                <CardFooter className="p-0">
                                    <div className="flex flex-col items-center justify-center my-8">
                                        <Button
                                            type="submit"
                                            variant="filled"
                                            size="lg"
                                            className="btn btn--cta rounded-full font-display text-2xl"
                                            disabled={!(isValid && dirty) || isSubmitting || resetBlocked}
                                        >
                                            {isSubmitting ? 'Setting...' : 'Set Password'}
                                        </Button>
                                    </div>
                                    <Link
                                        className="w-full text-center mt-2"
                                        to="/login"
                                    >
                                        <Typography
                                            className="my-2 btn btn--primary text-white"
                                            as="span"
                                        >
                                            Back to Login
                                        </Typography>
                                    </Link>
                                </CardFooter>
                            </Form>
                        )}
                    </Formik>
                </Card>
            </div>
        </div>
    );
}
